import { Link, useParams } from '@tanstack/react-router';
import { ExternalLinkIcon, ReceiptTextIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DetailShell, DetailSkeleton, FactList, OwnerCard } from '@/components/detail-shell';
import { StatusBadge } from '@/components/status-badge';
import { EmptyState, ErrorNotice } from '@/components/states';
import { LedgerTable } from '@/pages/UserDetailPage';
import { useReceiptDetail } from '@/lib/admin-queries';
import { formatTashkent } from '@/lib/time';
import { strings } from '@/i18n/strings';

const DASH = '—';

/**
 * Bitta chek — o'z sahifasida.
 *
 * Soliq sahifasiga havola ekranning eng ko'rinadigan joyida: moderatorning ishi ko'pincha
 * "da'vo qilingan summa haqiqiymi" degan savolga tayanadi, va unga javob faqat chekning O'Z
 * sahifasida. Ilgari bu havola panelda umuman yo'q edi — `receipts.url` bazada yotardi va uni
 * hech qaysi ekran ko'rsatmasdi.
 */
export function ReceiptDetailPage() {
  const { receiptId } = useParams({ from: '/shell/moderation/receipts/$receiptId' });
  const detail = useReceiptDetail(receiptId);

  const data = detail.data;

  return (
    <DetailShell
      title={strings.detail.receiptTitle}
      description={strings.detail.receiptSubtitle}
      backTo="/moderation"
      actions={
        data ? (
          <Button variant="outline" size="sm" asChild>
            {/*
              `rel="noreferrer"`: bu tashqi sayt, va unga panelning manzilini yuborish kerak
              emas.
            */}
            <a href={data.url} target="_blank" rel="noreferrer">
              <ExternalLinkIcon aria-hidden="true" />
              {strings.detail.receiptOpenSource}
            </a>
          </Button>
        ) : null
      }
    >
      {detail.isError ? <ErrorNotice error={detail.error} /> : null}
      {detail.isPending ? <DetailSkeleton /> : null}

      {data ? (
        <>
          <OwnerCard owner={data.owner} />

          <Card>
            <CardHeader>
              <CardTitle>{strings.detail.receiptTitle}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge>{data.receipt.status}</StatusBadge>
                {data.receipt.revoked ? (
                  <Badge variant="destructive">{strings.moderation.revoked}</Badge>
                ) : null}
                {data.ticket ? (
                  <Button size="sm" variant="ghost" asChild>
                    <Link to="/moderation/tickets/$ticketId" params={{ ticketId: data.ticket.id }}>
                      {strings.detail.linkedTicket}: {data.ticket.code}
                    </Link>
                  </Button>
                ) : null}
              </div>

              <FactList
                facts={[
                  [strings.moderation.columnTotal, data.receipt.totalAmount ?? DASH],
                  [strings.moderation.columnPoints, data.receipt.pointsAwarded ?? DASH],
                  [strings.moderation.columnVerifiedBy, data.receipt.totalVerifiedBy ?? DASH],
                  [strings.detail.receiptMerchant, data.receipt.merchantName ?? DASH],
                  [strings.detail.receiptIssuedAt, formatTashkent(data.receipt.issuedAt)],
                  [strings.moderation.columnScannedAt, formatTashkent(data.receipt.scannedAt)],
                  [strings.detail.receiptWeek, data.weekKey],
                  [strings.detail.receiptAttempts, data.receipt.detailsAttempts],
                  [
                    strings.detail.receiptFetchedAt,
                    data.detailsFetchedAt ? formatTashkent(data.detailsFetchedAt) : DASH,
                  ],
                  [
                    strings.detail.receiptRetryAt,
                    data.nextRetryAt ? formatTashkent(data.nextRetryAt) : DASH,
                  ],
                  [
                    strings.detail.receiptFiscalKey,
                    <span key="fiscal" className="font-mono text-xs">
                      {data.receipt.fiscalKey}
                    </span>,
                  ],
                  ...(data.revokedReason
                    ? ([[strings.detail.receiptRevokedReason, data.revokedReason]] as Array<
                        [string, string]
                      >)
                    : []),
                ]}
              />

              {data.receipt.lastError ? <ErrorNotice>{data.receipt.lastError}</ErrorNotice> : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{strings.detail.receiptItems}</CardTitle>
            </CardHeader>
            <CardContent>
              {data.items.length === 0 ? (
                <EmptyState title={strings.app.empty} icon={ReceiptTextIcon} />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{strings.detail.columnName}</TableHead>
                      <TableHead className="text-right">{strings.detail.columnQuantity}</TableHead>
                      <TableHead className="text-right">{strings.detail.columnPrice}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="tabular text-right">{item.quantity}</TableCell>
                        <TableCell className="tabular text-right">{item.price}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{strings.detail.ledgerHeading}</CardTitle>
            </CardHeader>
            <CardContent>
              {data.ledger.length === 0 ? (
                <EmptyState title={strings.app.empty} icon={ReceiptTextIcon} />
              ) : (
                <LedgerTable rows={data.ledger} />
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </DetailShell>
  );
}
